import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OfflineTimeChildren = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Family</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Offline Time with Children – Why It Matters
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>August 2, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>5 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/e7cdbc05-bdac-4206-b40e-fa62fbadead6.png" 
              alt="Father with two children reading by lantern light in a tent representing quality offline family time"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Children today grow up surrounded by screens. While technology has benefits, research warns of risks when screen time replaces face-to-face interactions and play. Offline time is not about restriction – it's about creating balance and presence.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The impact of screens on children</h2>

              <p className="mb-6 leading-relaxed">
                The American Academy of Pediatrics recommends limiting recreational screen time to <strong className="text-accent">no more than 2 hours per day for school-aged children</strong>. Excessive use is linked to attention difficulties, sleep disruption, and lower academic performance (AAP, 2016).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Benefits of offline time</h2>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">Strengthens emotional bonds</strong> with parents and caregivers.</li>
                <li><strong className="text-accent">Encourages creativity and problem-solving</strong> through unstructured play.</li>
                <li><strong className="text-accent">Supports physical health</strong> and outdoor activity.</li>
                <li><strong className="text-accent">Builds resilience and attention span</strong>.</li>
              </ul>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical ideas</h2>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">Designate family "offline hours"</strong> each day.</li>
                <li><strong className="text-accent">Encourage outdoor play, crafts, and reading</strong> together.</li>
                <li><strong className="text-accent">Model healthy digital habits</strong> as adults.</li>
              </ul>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p className="mb-6 leading-relaxed">
                Offline time is not about rejecting technology but about making room for the experiences children truly need: connection, play, and presence.
              </p>

              <p className="text-xl font-light italic text-accent mb-6">
                In moments unplugged, childhood finds its truest magic.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• American Academy of Pediatrics. (2016). Media and Young Minds. <em>Pediatrics, 138</em>(5), e20162591.</p>
                  <p>• Twenge, J. M., & Campbell, W. K. (2018). Associations between screen time and lower psychological well-being among children and adolescents: Evidence from a population-based study. <em>Preventive Medicine Reports, 12</em>, 271–283.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default OfflineTimeChildren;